import React from 'react';
import { useTranslation } from 'react-i18next';
import { AvailabilityEditor } from '../AvailabilityEditor';
import { useWorkloadStatistics } from '../../hooks/useWorkloadStats';
import type { User } from '../../types';
import './UserResponsibilityList.css';

interface UserResponsibilityState {
  userId: string;
  isResponsible: boolean;
  daysPerWeek: number; // 0.0-5.0
}

interface UserResponsibilityListProps {
  users: User[];
  userResponsibilities: Map<string, UserResponsibilityState>;
  onToggleResponsibility: (userId: string, isResponsible: boolean) => void;
  onAvailabilityChange: (userId: string, daysPerWeek: number) => void;
}

const UserResponsibilityList: React.FC<UserResponsibilityListProps> = ({
  users,
  userResponsibilities,
  onToggleResponsibility,
  onAvailabilityChange
}) => {
  const { t } = useTranslation();
  const { data: workloadStats, isLoading: workloadLoading } = useWorkloadStatistics();

  console.log('🔍 UserResponsibilityList - workloadStats:', workloadStats);
  console.log('🔍 UserResponsibilityList - users:', users.length);

  if (users.length === 0) {
    return (
      <div className="user-responsibility-list-empty">
        <p>{t('admin.userManagement.responsibilities.noUsers', 'Aucun utilisateur éligible')}</p>
      </div>
    );
  }

  const getDisplayName = (user: User) => {
    if (user.fmName) {
      return `${user.firstName} ${user.lastName} (${user.fmName})`;
    }
    return `${user.firstName} ${user.lastName}`;
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return 'user-responsibility-role-badge--admin';
      case 'bureau':
        return 'user-responsibility-role-badge--bureau';
      case 'expert':
        return 'user-responsibility-role-badge--expert';
      default:
        return 'user-responsibility-role-badge--default';
    }
  };

  const getWorkloadForUser = (userId: string) => {
    const found = workloadStats?.responsables.find(r => r.id === userId);
    console.log(`🔍 Workload for user ${userId}:`, found);
    return found;
  };

  const getWorkloadColor = (ratio: number) => {
    if (ratio < 1.5) return '#10b981'; // Green - low load
    if (ratio < 2.5) return '#f59e0b'; // Orange - medium load
    return '#ef4444'; // Red - high load
  };

  // Separate users into active responsables and available users
  const activeResponsables = users.filter(user => {
    const responsibility = userResponsibilities.get(user.id);
    return responsibility?.isResponsible;
  });

  const availableUsers = users.filter(user => {
    const responsibility = userResponsibilities.get(user.id);
    return !responsibility?.isResponsible;
  });

  const renderUserRow = (user: User, showWorkload: boolean) => {
    const responsibility = userResponsibilities.get(user.id);
    if (!responsibility) return null;

    const workload = getWorkloadForUser(user.id);
    const activeCount = workload?.current_active_affaire_count || 0;
    const workloadRatio = workload?.workload_ratio || 0;

    return (
      <div
        key={user.id}
        className={`user-responsibility-list-row ${
          responsibility.isResponsible ? 'user-responsibility-list-row--active' : ''
        } ${showWorkload ? '' : 'user-responsibility-list-row--compact'}`}
      >
        {/* Checkbox */}
        <div className="user-responsibility-list-cell user-responsibility-list-cell--checkbox">
          <label className="user-responsibility-checkbox">
            <input
              type="checkbox"
              checked={responsibility.isResponsible}
              onChange={(e) => onToggleResponsibility(user.id, e.target.checked)}
              className="user-responsibility-checkbox-input"
            />
            <span className="user-responsibility-checkbox-custom"></span>
          </label>
        </div>

        {/* User Info */}
        <div className="user-responsibility-list-cell user-responsibility-list-cell--user">
          <div className="user-responsibility-user-info">
            <div className="user-responsibility-user-name">{getDisplayName(user)}</div>
            <div className="user-responsibility-user-email">{user.email}</div>
          </div>
        </div>

        {/* Role Badge */}
        <div className="user-responsibility-list-cell user-responsibility-list-cell--role">
          <span className={`user-responsibility-role-badge ${getRoleBadgeClass(user.role)}`}>
            {t(`admin.userManagement.roles.${user.role}`, user.role)}
          </span>
        </div>

        {/* Availability Editor */}
        <div className="user-responsibility-list-cell user-responsibility-list-cell--availability">
          {responsibility.isResponsible ? (
            <AvailabilityEditor
              daysPerWeek={responsibility.daysPerWeek}
              onChange={(daysPerWeek) => onAvailabilityChange(user.id, daysPerWeek)}
            />
          ) : (
            <span className="user-responsibility-availability-disabled">—</span>
          )}
        </div>

        {/* Active Affaires Count - Only show for active responsables */}
        {showWorkload && (
          <div className="user-responsibility-list-cell user-responsibility-list-cell--workload">
            <div className="user-responsibility-workload-count">
              <span className="workload-count-value">{activeCount}</span>
              <span className="workload-count-label">
                {t('admin.userManagement.responsibilities.dossiers', 'dossiers')}
              </span>
            </div>
          </div>
        )}

        {/* Workload Ratio - Only show for active responsables */}
        {showWorkload && (
          <div className="user-responsibility-list-cell user-responsibility-list-cell--ratio">
            <div className="user-responsibility-workload-ratio">
              <div className="workload-ratio-bar-container">
                <div
                  className="workload-ratio-bar"
                  style={{
                    width: `${Math.min(workloadRatio / 3 * 100, 100)}%`,
                    backgroundColor: getWorkloadColor(workloadRatio)
                  }}
                />
              </div>
              <div className="workload-ratio-value">
                {workloadRatio.toFixed(2)}
                <span className="workload-ratio-unit">x</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="user-responsibility-lists-container">
      {/* Active Responsables Section */}
      {activeResponsables.length > 0 && (
        <div className="user-responsibility-section">
          <div className="user-responsibility-section-header">
            <h3 className="user-responsibility-section-title">
              {t('admin.userManagement.responsibilities.activeResponsables', 'Responsables Actifs')}
              <span className="user-responsibility-section-count">({activeResponsables.length})</span>
            </h3>
            {workloadStats && (
              <div className="user-responsibility-section-summary">
                <span className="summary-item">
                  <strong>{workloadStats.summary.total_active_affaires}</strong> dossiers actifs
                </span>
                <span className="summary-item">
                  Charge moyenne: <strong>{workloadStats.summary.average_ratio.toFixed(1)}x</strong>
                </span>
              </div>
            )}
          </div>

          <div className="user-responsibility-list">
            <div className="user-responsibility-list-header">
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--checkbox">
                {t('admin.userManagement.responsibilities.active', 'Actif')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--user">
                {t('admin.userManagement.responsibilities.user', 'Utilisateur')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--role">
                {t('admin.userManagement.responsibilities.role', 'Rôle')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--availability">
                {t('admin.userManagement.responsibilities.availability', 'Disponibilité')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--workload">
                {t('admin.userManagement.responsibilities.activeAffaires', 'Dossiers Actifs')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--ratio">
                {t('admin.userManagement.responsibilities.workloadRatio', 'Charge')}
              </div>
            </div>

            <div className="user-responsibility-list-body">
              {activeResponsables.map(user => renderUserRow(user, true))}
            </div>
          </div>
        </div>
      )}

      {/* Available Users Section */}
      {availableUsers.length > 0 && (
        <div className="user-responsibility-section">
          <div className="user-responsibility-section-header">
            <h3 className="user-responsibility-section-title">
              {t('admin.userManagement.responsibilities.availableUsers', 'Utilisateurs Disponibles')}
              <span className="user-responsibility-section-count">({availableUsers.length})</span>
            </h3>
            <p className="user-responsibility-section-description">
              {t('admin.userManagement.responsibilities.availableUsersDesc', 'Utilisateurs éligibles non assignés actuellement')}
            </p>
          </div>

          <div className="user-responsibility-list user-responsibility-list--compact">
            <div className="user-responsibility-list-header">
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--checkbox">
                {t('admin.userManagement.responsibilities.assign', 'Assigner')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--user">
                {t('admin.userManagement.responsibilities.user', 'Utilisateur')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--role">
                {t('admin.userManagement.responsibilities.role', 'Rôle')}
              </div>
              <div className="user-responsibility-list-header-cell user-responsibility-list-header-cell--availability">
                {t('admin.userManagement.responsibilities.availability', 'Disponibilité')}
              </div>
            </div>

            <div className="user-responsibility-list-body">
              {availableUsers.map(user => renderUserRow(user, false))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { UserResponsibilityList };
export default UserResponsibilityList;
